// /app/frontend/src/components/frugal-v2/components/ReceiptScanner.jsx
// Receipt photo scanner with OCR (< 150 lines)

import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Camera, X, Loader2, Check, AlertCircle } from 'lucide-react';
import * as api from '../services/frugalApi';
import { EXPENSE_CATEGORIES } from '../constants/categories';

// Compress image using canvas to avoid 502 timeout errors
const compressImage = (file, maxWidth = 1024, quality = 0.7) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let { width, height } = img;
      
      // Scale down if larger than maxWidth
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }
      
      canvas.width = width;
      canvas.height = height;
      
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      
      // Convert to JPEG with compression
      const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
      console.log(`📸 Image compressed: ${Math.round(compressedBase64.length / 1024)}KB`);
      resolve(compressedBase64);
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
};

const ReceiptScanner = ({ isOpen, onClose, onResult, token, t }) => {
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file type
    if (!file.type.startsWith('image/')) {
      setError(t('frugal.receipt.errorImage'));
      return;
    }

    try {
      // Compress image before setting state
      const compressedImage = await compressImage(file, 1024, 0.7);
      setPreview(compressedImage);
      setImage(compressedImage);
      setError(null);
      setResult(null);
    } catch (err) {
      console.error('Error compressing image:', err);
      setError(t('frugal.receipt.errorImage'));
    }
  };

  const handleAnalyze = async () => {
    if (!image) return;

    setAnalyzing(true);
    setError(null);

    try {
      const response = await api.analyzeReceipt(token, image);
      
      if (response.success) {
        setResult(response);
      } else {
        setError(response.error || t('frugal.receipt.errorAnalyze'));
      }
    } catch (err) {
      setError(t('frugal.receipt.errorAnalyze') + ': ' + err.message);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleConfirm = () => {
    if (result) {
      onResult({
        name: result.merchant,
        amount: result.amount,
        category: result.category,
        date: result.date
      });
      handleClose();
    }
  };

  const handleClose = () => {
    setImage(null);
    setPreview(null);
    setResult(null);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
      <div className="bg-slate-900 rounded-2xl w-full max-w-md max-h-[90vh] overflow-hidden border border-white/10">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Camera className="w-5 h-5 text-emerald-400" />
            <h2 className="text-lg font-semibold text-white">
              {t('frugal.receipt.title', 'Escanear Recibo')}
            </h2>
          </div>
          <button onClick={handleClose} className="p-2 hover:bg-white/10 rounded-lg">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4 overflow-y-auto max-h-[70vh]">
          {/* Image Preview or Upload Button */}
          {preview ? (
            <div className="relative">
              <img src={preview} alt="Recibo" className="w-full rounded-lg max-h-48 object-contain bg-black" />
              <button 
                onClick={() => { setPreview(null); setImage(null); setResult(null); }}
                className="absolute top-2 right-2 p-1 bg-black/50 rounded-full hover:bg-black/70"
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-full py-12 border-2 border-dashed border-white/20 rounded-xl hover:border-emerald-500/50 transition-colors flex flex-col items-center gap-3"
            >
              <Camera className="w-10 h-10 text-slate-500" />
              <span className="text-slate-400">{t('frugal.receipt.tapToScan', 'Toca para tomar foto o seleccionar')}</span>
            </button>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFileSelect}
            className="hidden"
          />

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-rose-500/20 rounded-lg text-rose-400 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Result */}
          {result && (
            <div className="space-y-3 p-4 bg-emerald-500/10 rounded-xl border border-emerald-500/30">
              <div className="flex items-center gap-2 text-emerald-400 font-medium">
                <Check className="w-5 h-5" />
                {t('frugal.receipt.detected')}
                <span className="text-xs text-slate-500 ml-auto">
                  {Math.round(result.confidence * 100)}% {t('frugal.receipt.confidence')}
                </span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-400">{t('frugal.receipt.merchant')}:</span>
                  <span className="text-white font-medium">{result.merchant}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">{t('frugal.receipt.amount')}:</span>
                  <span className="text-emerald-400 font-bold text-lg">${result.amount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">{t('frugal.receipt.category')}:</span>
                  <span className="text-white">{result.category}</span>
                </div>
                {result.date && (
                  <div className="flex justify-between">
                    <span className="text-slate-400">{t('frugal.receipt.date')}:</span>
                    <span className="text-white">{result.date}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="p-4 border-t border-white/10 flex gap-3">
          {!result ? (
            <button
              onClick={handleAnalyze}
              disabled={!image || analyzing}
              className="flex-1 py-3 bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-700 disabled:text-slate-500 text-white rounded-xl font-medium flex items-center justify-center gap-2"
            >
              {analyzing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t('frugal.receipt.analyzing', 'Analizando...')}
                </>
              ) : (
                t('frugal.receipt.analyze', 'Analizar Recibo')
              )}
            </button>
          ) : (
            <>
              <button
                onClick={() => { setResult(null); setPreview(null); setImage(null); }}
                className="flex-1 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl font-medium"
              >
                {t('frugal.receipt.retry', 'Reintentar')}
              </button>
              <button
                onClick={handleConfirm}
                className="flex-1 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-medium"
              >
                {t('frugal.receipt.confirm', 'Agregar Gasto')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ReceiptScanner;
